/**
 * The seven set-up steps (PRD F-04) and what the brand panel says on each: Figma "ONB-00" to "ONB-07"
 * (UK Real Estate: 4884:26530, 4884:26610, 4259:4826, 4236:2, 4236:102, 4237:2, 4237:126).
 */
import type { CSSProperties } from "react";

export const STEP_COUNT = 7;

/** An illustration, cropped inside its frame exactly as in Figma. */
export interface StepArt {
  src: string;
  width: number;
  height: number;
  crop: CSSProperties;
}

export interface StepInfo {
  label: string;
  headline: string;
  body: string;
  /** Title line height, as each frame sets it. */
  leading: string;
  art: StepArt;
}

const art = (n: number, width: number, height: number, crop: CSSProperties): StepArt => ({
  src: `/illustrations/growth-onboarding/step-${n}.png`,
  width,
  height,
  crop,
});

export const STEPS: StepInfo[] = [
  {
    label: "Terms & data use",
    headline: "Before we build your intelligence profile",
    body: "We're governed by clear rules on sources, evidence and how far AI interpretation can go. Here's what that means for you.",
    leading: "1.4",
    art: art(1, 130, 77, { height: "137.08%", left: "-12.55%", top: "-19.68%", width: "122.78%" }),
  },
  {
    label: "Choose business profile",
    headline: "One profile, tuned to your industry",
    body: "Sources, topics, language and risk controls are all shaped around the niche you choose here.",
    leading: "1.4",
    art: art(2, 130, 86.064, { height: "134.38%", left: "-16.59%", top: "-17.19%", width: "133.45%" }),
  },
  {
    label: "Business context",
    headline: "The details that make this yours",
    body: "Vague answers mean vague alerts. The more specific you are here, the sharper every insight and recommendation will be.",
    leading: "1.4",
    art: art(3, 151, 113, { height: "118.24%", left: "-15.34%", top: "-10.85%", width: "133.1%" }),
  },
  {
    label: "Choose topics",
    headline: "Only what matters to you",
    body: "Weavex only surfaces developments in the topics you choose — nothing else clutters your dashboard.",
    leading: "normal",
    art: art(4, 126, 109, { height: "133.83%", left: "-9.68%", top: "-17.93%", width: "115.58%" }),
  },
  {
    label: "Add competitors",
    headline: "See what your competitors show publicly",
    body: "We only ever look at what's publicly visible — never private performance data.",
    leading: "1.2",
    art: art(5, 145, 87, { height: "117.7%", left: "-2.35%", top: "-6.09%", width: "106.08%" }),
  },
  {
    label: "Notification setup",
    headline: "Alerts on your terms",
    body: "Choose how and when you hear about changes — we'll only override this for security or operational essentials.",
    leading: "normal",
    art: art(6, 132, 110, { height: "143.31%", left: "-8.94%", top: "-23.31%", width: "119.2%" }),
  },
  {
    label: "Review",
    headline: "Last look before we start",
    body: "Once confirmed, we'll set up your dashboard using exactly what you've told us here.",
    leading: "normal",
    art: art(7, 173, 137, { height: "142.02%", left: "-6.38%", top: "-21.4%", width: "112.67%" }),
  },
];

/** Where each step lives. `?from=review` sends Continue back to the review (PRD: "Edit returns to that step"). */
export const setupPath = (step: number, fromReview = false) =>
  `/growth-intelligence/setup/${step}${fromReview ? "?from=review" : ""}`;
